import React, { useState, useEffect } from "react";
import { Typography, Card, Button, Space, Form, Input, Row, Col, Collapse, Tag, Divider, message, Descriptions, Alert, Spin, Tabs } from "antd";
import { CheckOutlined, CloseOutlined, EditOutlined, InfoCircleOutlined, FileTextOutlined, BulbOutlined, ArrowRightOutlined, MessageOutlined, RobotOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { processActionItem } from "../../api/actionItemApi";
import { getActionHandlerComponent } from "../../features/workspace/NotificationCenter/handlers";
import { ProcessingStage, ResolutionStatus, ActionItemStatus, ActionItemResolution, SourceType, ActionType, SOURCE_TYPE_LABELS, ACTION_TYPE_LABELS, PROCESSING_STAGE_INFO, RESOLUTION_STATUS_INFO, ACTION_ITEM_STATUS_INFO, ACTION_ITEM_RESOLUTION_INFO } from "../../features/workspace/NotificationCenter/constants/actionItemConstants";

const { Title, Text, Paragraph } = Typography;
const { Panel } = Collapse;
const { TextArea } = Input;
const { TabPane } = Tabs;

/**
 * 行动项详情视图组件
 * 用于展示和处理单个行动项的详情
 *
 * @param {Object} props
 * @param {Object} props.actionItem - 行动项数据
 * @param {Object} props.currentFlightData - 当前航班数据
 * @param {Function} props.onProcessed - 处理完成后的回调
 * @param {boolean} props.flightDataLoading - 航班数据是否正在加载
 * @param {Object} props.airportsMap - 机场数据映射，用于时区转换
 */
const NotificationDetailView = ({ actionItem, currentFlightData, onProcessed, flightDataLoading, airportsMap = {} }) => {
    const [loading, setLoading] = useState(false);
    const [remarksForm] = Form.useForm();
    const [modifiedParameters, setModifiedParameters] = useState(null);
    const [activeTab, setActiveTab] = useState("details"); // details或source

    // 重置状态
    useEffect(() => {
        setModifiedParameters(null);
        remarksForm.resetFields();
        setActiveTab("details"); // 重置为details标签页
    }, [actionItem, remarksForm]);

    if (!actionItem) {
        return (
            <Card>
                <div style={{ padding: "24px", textAlign: "center" }}>
                    <Text type="secondary">请在左侧选择一个通知项</Text>
                </div>
            </Card>
        );
    }

    // 从行动项数据中获取关键信息
    const currentActionType = actionItem.suggested_payload?.action_type || actionItem.suggested_change_type;

    // V4.1 更新：判断是否是已处理的行动项
    const isResolved =
        // 新字段判断
        [ActionItemStatus.COMPLETED, ActionItemStatus.FAILED, ActionItemStatus.REJECTED].includes(actionItem.status) ||
        [ActionItemResolution.AUTO_EXECUTED, ActionItemResolution.USER_CONFIRMED, ActionItemResolution.USER_MODIFIED, ActionItemResolution.USER_REJECTED].includes(actionItem.resolution) ||
        // 兼容旧字段判断
        actionItem.resolution_status === ResolutionStatus.USER_CONFIRMED ||
        actionItem.resolution_status === ResolutionStatus.USER_MODIFIED ||
        actionItem.resolution_status === ResolutionStatus.USER_REJECTED ||
        actionItem.resolution_status === ResolutionStatus.AUTO_RESOLVED ||
        actionItem.resolution_status === ResolutionStatus.CONFIRMED_AUTO ||
        actionItem.processing_stage === ProcessingStage.AUTO_SUCCESS ||
        actionItem.processing_stage === ProcessingStage.USER_SUCCESS ||
        actionItem.processing_stage === ProcessingStage.CLOSED_FAILED;

    // 获取航班号和航段信息 - 优先使用 currentFlightData 如果可用且完整，否则回退
    let flightNumber =
        currentFlightData?.flight_number ||
        actionItem.suggested_payload?.context_data_summary?.flight_details?.flight_number ||
        actionItem.suggested_payload?.action_parameters?.flight_number || // 回退，虽然不太理想
        "未知航班";
    let flightRoute = "";
    const depAirportContext = actionItem.suggested_payload?.context_data_summary?.flight_details?.departure_airport;
    const arrAirportContext = actionItem.suggested_payload?.context_data_summary?.flight_details?.arrival_airport;

    const depAirport = currentFlightData?.departure_airport || depAirportContext || actionItem.suggested_payload?.action_parameters?.departure_airport;
    const arrAirport = currentFlightData?.arrival_airport || arrAirportContext || actionItem.suggested_payload?.action_parameters?.arrival_airport;

    if (depAirport && arrAirport) {
        flightRoute = `${depAirport}-${arrAirport}`;
    }

    // 生成事件描述
    let eventDescription = "";
    if (actionItem.source_type === SourceType.FLIGHT_MESSAGE_MVT && currentActionType && currentActionType.includes("UPDATE_OPERATION_FLIGHT_FROM_MVT")) {
        const params = actionItem.suggested_payload?.action_parameters || {};
        if (params.new_atd_utc) {
            eventDescription = `系统收到MVT报文，报告航班 ${flightNumber} (${flightRoute}) 于 ${dayjs.utc(params.new_atd_utc).format("YYYY-MM-DD HH:mm")}Z 实际撤轮挡 (Off-block)`;
            if (params.new_atd_icao_utc) {
                eventDescription += ` | 实际离地 (Airborne): ${dayjs.utc(params.new_atd_icao_utc).format("YYYY-MM-DD HH:mm")}Z`;
            }
            if (params.delay_code) {
                eventDescription += `，延误代码 ${params.delay_code}`;
            }
            const delayMinutes = params.delay_minutes || params.mvt_full_data?.delay_duration;
            if (delayMinutes) {
                eventDescription += ` (${delayMinutes}分钟)`;
            }
            eventDescription += "。";
        } else if (params.new_ata_utc) {
            eventDescription = `系统收到MVT报文，报告航班 ${flightNumber} (${flightRoute}) 于 ${dayjs.utc(params.new_ata_utc).format("YYYY-MM-DD HH:mm")}Z 实际到达 (On-block)`;
            if (params.new_ata_icao_utc) {
                eventDescription += ` | 实际落地 (Touch-down): ${dayjs.utc(params.new_ata_icao_utc).format("YYYY-MM-DD HH:mm")}Z`;
            }
            eventDescription += "。";
        } else if (params.new_etd_utc) {
            eventDescription = `系统收到MVT报文，航班 ${flightNumber} (${flightRoute}) 预计起飞时间 (ETD) 更新为 ${dayjs.utc(params.new_etd_utc).format("YYYY-MM-DD HH:mm")}Z。`;
        } else if (params.new_eta_utc) {
            eventDescription = `系统收到MVT报文，航班 ${flightNumber} (${flightRoute}) 预计到达时间 (ETA) 更新为 ${dayjs.utc(params.new_eta_utc).format("YYYY-MM-DD HH:mm")}Z。`;
        }
    } else if (actionItem.source_type === SourceType.FLIGHT_MESSAGE_MVT && actionItem.ai_processing_notes?.includes("无法找到匹配的执行航班")) {
        eventDescription = "收到MVT报文，但未找到匹配的系统航班记录，需要手动关联。";
    } else if (actionItem.source_type === SourceType.FLIGHT_MESSAGE_MVT && actionItem.ai_processing_notes?.includes("MVT解析失败")) {
        eventDescription = "收到MVT报文，但系统无法自动解析，需要人工审核。";
    }

    // 创建行动项标题
    let actionTitle = "";
    if (actionItem.source_type === SourceType.FLIGHT_MESSAGE_MVT) {
        if (currentActionType && currentActionType.includes("UPDATE_OPERATION_FLIGHT_FROM_MVT")) {
            actionTitle = `MVT报文待处理: ${flightNumber} (${flightRoute}) 时刻更新建议`;
        } else if (actionItem.ai_processing_notes?.includes("无法找到匹配的执行航班")) {
            const mvtFlightNum = actionItem.parsed_data?.flight_number || actionItem.suggested_payload?.context_data_summary?.parsed_mvt_data?.flight_number || "";
            const mvtDate = actionItem.parsed_data?.flight_date || actionItem.suggested_payload?.context_data_summary?.parsed_mvt_data?.flight_date || "";
            actionTitle = `MVT报文待处理: 需关联航班 (解析航班号 ${mvtFlightNum}${mvtDate ? ", 日期 " + dayjs(mvtDate).format("YYYY-MM-DD") : ""})`;
        } else if (actionItem.ai_processing_notes?.includes("MVT解析失败")) {
            actionTitle = "MVT报文处理异常: 解析失败";
        } else {
            actionTitle = "MVT报文待处理";
        }
    } else {
        actionTitle = `${SOURCE_TYPE_LABELS[actionItem.source_type] || actionItem.source_type} 待处理`;
    }

    // 如果是已处理的行动项，添加标记
    if (isResolved) {
        actionTitle = `[已处理] ${actionTitle}`;
    }

    // V4.1 更新：判断当前处理阶段和问题摘要
    let stageSummary = "";
    let stageAlertInfo = { message: "", type: "info" };

    if (isResolved) {
        if (actionItem.resolution_status === ResolutionStatus.USER_CONFIRMED) {
            stageAlertInfo = { message: RESOLUTION_STATUS_INFO[ResolutionStatus.USER_CONFIRMED]?.label || "用户已采纳建议", type: "success" };
        } else if (actionItem.resolution_status === ResolutionStatus.USER_MODIFIED) {
            stageAlertInfo = { message: RESOLUTION_STATUS_INFO[ResolutionStatus.USER_MODIFIED]?.label || "用户修改后采纳", type: "success" };
        } else if (actionItem.resolution_status === ResolutionStatus.USER_REJECTED) {
            stageAlertInfo = { message: RESOLUTION_STATUS_INFO[ResolutionStatus.USER_REJECTED]?.label || "用户已拒绝", type: "warning" };
        } else if (actionItem.resolution_status === ResolutionStatus.AUTO_RESOLVED) {
            stageAlertInfo = { message: RESOLUTION_STATUS_INFO[ResolutionStatus.AUTO_RESOLVED]?.label || "系统自动处理", type: "success" };
        } else if (actionItem.resolution_status === ResolutionStatus.CONFIRMED_AUTO || actionItem.processing_stage === ProcessingStage.AUTO_CONFIRMED) {
            stageAlertInfo = { message: RESOLUTION_STATUS_INFO[ResolutionStatus.CONFIRMED_AUTO]?.label || "系统自动确认", type: "success" };
        } else if (actionItem.processing_stage === ProcessingStage.AUTO_SUCCESS) {
            stageAlertInfo = { message: PROCESSING_STAGE_INFO[ProcessingStage.AUTO_SUCCESS]?.label || "系统执行成功", type: "success" };
        } else if (actionItem.processing_stage === ProcessingStage.USER_SUCCESS) {
            stageAlertInfo = { message: PROCESSING_STAGE_INFO[ProcessingStage.USER_SUCCESS]?.label || "用户操作成功", type: "success" };
        } else if (actionItem.processing_stage === ProcessingStage.CLOSED_FAILED) {
            stageAlertInfo = { message: PROCESSING_STAGE_INFO[ProcessingStage.CLOSED_FAILED]?.label || "执行失败", type: "error" };
        } else {
            stageAlertInfo = { message: "已处理", type: "success" };
        }
    } else if (actionItem.processing_stage === ProcessingStage.PENDING_MANUAL_AI) {
        stageAlertInfo = { message: PROCESSING_STAGE_INFO[ProcessingStage.PENDING_MANUAL_AI]?.label || "待审核(AI建议)", type: "info" };
    } else if (actionItem.processing_stage === ProcessingStage.PENDING_MANUAL_RAW) {
        stageAlertInfo = { message: PROCESSING_STAGE_INFO[ProcessingStage.PENDING_MANUAL_RAW]?.label || "待审核(原始输入)", type: "warning" };
    } else if (actionItem.processing_stage === ProcessingStage.PENDING_AI) {
        stageAlertInfo = { message: PROCESSING_STAGE_INFO[ProcessingStage.PENDING_AI]?.label || "AI分析中", type: "info" };
    } else {
        stageAlertInfo = { message: "待处理", type: "info" };
    }
    stageSummary = stageAlertInfo.message;

    // V3.0 更新：处理确认按钮点击
    const handleConfirm = async (processRequest = null) => {
        setLoading(true);
        try {
            let requestData;

            if (processRequest) {
                // 来自通用处理器的请求
                requestData = processRequest;
            } else {
                // 来自传统按钮的请求
                const remarks = remarksForm.getFieldValue("remarks");
                requestData = {
                    action_taken: "USER_CONFIRMED",
                    user_remarks: remarks || "",
                };
            }

            await processActionItem(actionItem.id, requestData);
            message.success("已确认处理完成");
            if (onProcessed) onProcessed();
        } catch (error) {
            message.error("处理失败: " + (error.message || "未知错误"));
        } finally {
            setLoading(false);
        }
    };

    // 处理修改后确认按钮点击
    const handleModifiedConfirm = async () => {
        // if (!modifiedParameters) {
        //     message.warning("请先修改参数");
        //     return;
        // }

        // setLoading(true);
        // try {
        //     const remarks = remarksForm.getFieldValue("remarks");
        //     await processActionItem(actionItem.id, {
        //         action_taken: "USER_MODIFIED_CONFIRMED",
        //         user_modified_payload: {
        //             ...actionItem.suggested_payload,
        //             action_parameters: modifiedParameters,
        //             reasoning: `用户修改并确认: ${remarks || "无备注"}`,
        //         },
        //         user_remarks: remarks || "",
        //     });
        //     message.success("已确认处理完成");
        //     if (onProcessed) onProcessed();
        // } catch (error) {
        //     message.error("处理失败: " + (error.message || "未知错误"));
        // } finally {
        //     setLoading(false);
        // }
        message.info("功能正在测试中");
    };

    // V3.0 更新：处理拒绝按钮点击
    const handleReject = async (processRequest = null) => {
        setLoading(true);
        try {
            let requestData;

            if (processRequest) {
                // 来自通用处理器的请求
                requestData = processRequest;
            } else {
                // 来自传统按钮的请求
                const remarks = remarksForm.getFieldValue("remarks");
                requestData = {
                    action_taken: "USER_REJECTED",
                    user_remarks: remarks || "用户拒绝，无特定原因",
                };
            }

            await processActionItem(actionItem.id, requestData);
            message.success("已标记为拒绝");
            if (onProcessed) onProcessed();
        } catch (error) {
            message.error("处理失败: " + (error.message || "未知错误"));
        } finally {
            setLoading(false);
        }
    };

    // 处理参数变化
    const handleParametersChange = (updatedParams) => {
        setModifiedParameters(updatedParams);
    };

    const mvtMessageType = actionItem?.suggested_payload?.context_data_summary?.mvt_message_type;

    // 处理链接并确认
    const handleLinkAndConfirm = async (linkParams) => {
        // setLoading(true);
        // try {
        //     const remarks = remarksForm.getFieldValue("remarks");
        //     await processActionItem(actionItem.id, {
        //         action_taken: "USER_LINKED_AND_CONFIRMED",
        //         user_modified_payload: {
        //             action_type: "UPDATE_OPERATION_FLIGHT_FROM_MVT",
        //             action_parameters: linkParams,
        //             reasoning: `用户关联航班并确认: ${remarks || "无备注"}`,
        //         },
        //         user_remarks: remarks || "",
        //     });
        //     message.success("已关联航班并处理完成");
        //     if (onProcessed) onProcessed();
        // } catch (error) {
        //     message.error("处理失败: " + (error.message || "未知错误"));
        // } finally {
        //     setLoading(false);
        // }
        message.info("功能正在测试中");
    };

    // 是否已经修改过参数
    const isModified = !!modifiedParameters;

    // 渲染原始数据和AI建议理由
    const renderSourceAndReasoning = () => {
        return (
            <div className="source-reasoning-container">
                {actionItem.raw_input_content && (
                    <div className="source-data-section">
                        <Title level={5} style={{ display: "flex", alignItems: "center" }}>
                            <FileTextOutlined style={{ marginRight: 8 }} /> 原始MVT报文
                        </Title>
                        <div
                            style={{
                                padding: "16px",
                                background: "#f5f5f5",
                                border: "1px solid #d9d9d9",
                                borderRadius: "4px",
                                marginBottom: "20px",
                                fontFamily: "monospace",
                                whiteSpace: "pre-wrap",
                                fontSize: "14px",
                                lineHeight: "1.6",
                                maxHeight: "300px",
                                overflowY: "auto",
                            }}
                        >
                            {actionItem.raw_input_content}
                        </div>
                    </div>
                )}

                {actionItem.ai_processing_notes && (
                    <div className="ai-notes-section">
                        <Title level={5} style={{ display: "flex", alignItems: "center" }}>
                            <BulbOutlined style={{ marginRight: 8 }} /> AI处理过程
                        </Title>
                        <div
                            style={{
                                padding: "16px",
                                background: "#fffbe6",
                                border: "1px solid #ffe58f",
                                borderRadius: "4px",
                                marginBottom: "20px",
                                whiteSpace: "pre-line",
                                maxHeight: "300px",
                                overflowY: "auto",
                            }}
                        >
                            {actionItem.ai_processing_notes}
                        </div>
                    </div>
                )}

                {actionItem.suggested_payload?.reasoning && (
                    <div className="suggestion-reason-section">
                        <Title level={5} style={{ display: "flex", alignItems: "center" }}>
                            <MessageOutlined style={{ marginRight: 8 }} /> 系统建议理由
                        </Title>
                        <Alert
                            message={actionItem.suggested_payload.reasoning}
                            type="info"
                            showIcon
                            style={{
                                marginBottom: "20px",
                                padding: "12px 16px",
                                borderRadius: "4px",
                            }}
                        />
                    </div>
                )}
            </div>
        );
    };

    // 渲染操作按钮组
    const renderActionButtons = () => {
        if (isResolved) return null;

        return (
            <Row gutter={16} style={{ marginTop: "16px" }}>
                {actionItem.source_type === SourceType.FLIGHT_MESSAGE_MVT && (
                    <>
                        {currentActionType && (currentActionType === ActionType.UPDATE_OPERATION_FLIGHT || currentActionType === ActionType.UPDATE_OPERATION_FLIGHT_FROM_MVT) && (
                            <>
                                <Col span={8}>
                                    <Button
                                        type="primary"
                                        icon={<CheckOutlined />}
                                        block
                                        onClick={handleConfirm}
                                        loading={loading}
                                        disabled={isModified}
                                        style={{
                                            height: "40px",
                                            boxShadow: "0 2px 0 rgba(0,0,0,0.045)",
                                        }}
                                    >
                                        采纳系统建议
                                    </Button>
                                </Col>
                                <Col span={8}>
                                    <Button
                                        type={isModified ? "primary" : "default"}
                                        icon={<EditOutlined />}
                                        block
                                        onClick={handleModifiedConfirm}
                                        loading={loading}
                                        disabled={!isModified}
                                        style={{
                                            backgroundColor: isModified ? "#e6f7ff" : undefined,
                                            borderColor: isModified ? "#1890ff" : undefined,
                                            color: isModified ? "#1890ff" : undefined,
                                            height: "40px",
                                            boxShadow: isModified ? "0 2px 0 rgba(24,144,255,0.1)" : undefined,
                                        }}
                                    >
                                        修改并采纳
                                    </Button>
                                </Col>
                            </>
                        )}
                        <Col span={8}>
                            <Button
                                danger
                                icon={<CloseOutlined />}
                                block
                                onClick={handleReject}
                                loading={loading}
                                style={{
                                    height: "40px",
                                    boxShadow: "0 2px 0 rgba(0,0,0,0.045)",
                                }}
                            >
                                拒绝
                            </Button>
                        </Col>
                    </>
                )}
            </Row>
        );
    };

    return (
        <Card className="notification-detail-card" style={{ boxShadow: "0 1px 2px rgba(0,0,0,0.03)" }}>
            {flightDataLoading ? (
                <div style={{ textAlign: "center", padding: "50px 0" }}>
                    <Spin tip="加载航班信息中..." />
                </div>
            ) : (
                <>
                    {/* 区域 A: 上下文信息区 - 强化的行动项概要与状态 */}
                    <div
                        className="context-header"
                        style={{
                            marginBottom: "20px",
                            padding: "16px",
                            background: isResolved ? "#fafafa" : "#f9f9f9",
                            borderRadius: "4px",
                            border: isResolved ? "1px solid #e8e8e8" : "1px solid #d9e3f0",
                            boxShadow: isResolved ? "none" : "0 1px 2px rgba(0,0,0,0.03)",
                        }}
                    >
                        <Row>
                            <Col span={24}>
                                <Title level={4} style={{ marginBottom: "8px", color: isResolved ? "#8c8c8c" : "#262626" }}>
                                    {actionTitle}
                                </Title>
                                <Space style={{ marginBottom: "12px" }} wrap>
                                    <Tag color="blue">{actionItem.source_type === SourceType.FLIGHT_MESSAGE_MVT && mvtMessageType ? `${SOURCE_TYPE_LABELS[actionItem.source_type] || actionItem.source_type}-${mvtMessageType}` : SOURCE_TYPE_LABELS[actionItem.source_type] || actionItem.source_type}</Tag>
                                    {currentActionType && <Tag color="green">{ACTION_TYPE_LABELS[currentActionType] || currentActionType}</Tag>}
                                    {actionItem.priority && <Tag color={actionItem.priority === 1 ? "red" : actionItem.priority === 2 ? "orange" : "default"}>优先级: {actionItem.priority === 1 ? "高" : actionItem.priority === 2 ? "中" : "低"}</Tag>}
                                </Space>
                            </Col>
                        </Row>

                        {(flightNumber !== "未知航班" || flightRoute) && (
                            <Descriptions size="small" column={3} style={{ marginTop: "8px" }}>
                                <Descriptions.Item label="航班号">{flightNumber}</Descriptions.Item>
                                {flightRoute && <Descriptions.Item label="航线">{flightRoute}</Descriptions.Item>}
                                <Descriptions.Item label="接收时间">{dayjs(actionItem.created_at).format("YYYY-MM-DD HH:mm:ss")}</Descriptions.Item>
                                {currentFlightData?.std && <Descriptions.Item label="计划起飞">{dayjs.utc(currentFlightData.std).format("MM-DD HH:mm")}Z</Descriptions.Item>}
                                {currentFlightData?.sta && <Descriptions.Item label="计划到达">{dayjs.utc(currentFlightData.sta).format("MM-DD HH:mm")}Z</Descriptions.Item>}
                            </Descriptions>
                        )}

                        {eventDescription && <Alert message={eventDescription} type="info" style={{ marginTop: "8px", backgroundColor: "#e6f7ff", border: "1px solid #91d5ff" }} />}
                    </div>

                    {/* 切换标签页 - AI建议查看与原始数据 */}
                    <Tabs
                        activeKey={activeTab}
                        onChange={setActiveTab}
                        className="notification-detail-tabs"
                        style={{ marginBottom: "16px" }}
                        tabBarStyle={{
                            marginBottom: "16px",
                            borderBottom: "1px solid #f0f0f0",
                            fontWeight: "bold",
                        }}
                        items={[
                            {
                                key: "details",
                                label: (
                                    <span>
                                        <EditOutlined /> AI建议与操作
                                    </span>
                                ),
                                children: (
                                    <>
                                        {/* 区域 B: 核心信息展示与交互区 */}
                                        <div
                                            className="notification-detail-content"
                                            style={{
                                                marginBottom: "24px",
                                                background: "#fafcff",
                                                border: "1px solid #e6f0fa",
                                                borderRadius: "4px",
                                                padding: "16px",
                                                position: "relative",
                                                boxShadow: "0 1px 3px rgba(0,47,167,0.05)",
                                            }}
                                        >
                                            {/* V5.0 更新：使用简化的处理器组件调用 */}
                                            {(() => {
                                                const HandlerComponent = getActionHandlerComponent(actionItem);
                                                if (!HandlerComponent) {
                                                    return <div>未找到合适的处理器</div>;
                                                }
                                                return (
                                                    <HandlerComponent
                                                        actionItem={actionItem}
                                                        onConfirm={handleConfirm}
                                                        onReject={handleReject}
                                                        loading={loading}
                                                        readOnly={isResolved}
                                                        // 为兼容旧处理器保留的额外参数
                                                        currentFlightData={currentFlightData}
                                                        onParametersChange={handleParametersChange}
                                                        onLinkAndConfirm={handleLinkAndConfirm}
                                                        airportsMap={airportsMap}
                                                    />
                                                );
                                            })()}
                                        </div>

                                        {/* 区域 C: 用户操作与备注区 */}
                                        {!isResolved && (
                                            <div
                                                className="user-action-section"
                                                style={{
                                                    marginTop: "24px",
                                                    padding: "20px",
                                                    background: "#fafafa",
                                                    borderRadius: "4px",
                                                    border: "1px solid #f0f0f0",
                                                    boxShadow: "0 1px 2px rgba(0,0,0,0.02)",
                                                }}
                                            >
                                                <Form form={remarksForm} layout="vertical">
                                                    <Form.Item name="remarks" label={<Text strong>处理备注</Text>} style={{ marginBottom: "16px" }}>
                                                        <TextArea rows={2} placeholder="可选: 填写您对此通知的处理备注" style={{ borderRadius: "4px" }} />
                                                    </Form.Item>
                                                </Form>

                                                {renderActionButtons()}
                                            </div>
                                        )}
                                    </>
                                ),
                            },
                            {
                                key: "source",
                                label: (
                                    <span>
                                        <FileTextOutlined /> 原始数据和处理过程
                                    </span>
                                ),
                                children: renderSourceAndReasoning(),
                            },
                        ]}
                    />

                    {/* 如果是已处理状态，显示处理结果 */}
                    {isResolved && (
                        <div
                            className="resolution-info"
                            style={{
                                marginTop: "24px",
                                padding: "16px",
                                background: actionItem.resolution_status === ResolutionStatus.USER_REJECTED ? "#fff2f0" : "#f6ffed",
                                borderRadius: "4px",
                                border: actionItem.resolution_status === ResolutionStatus.USER_REJECTED ? "1px solid #ffccc7" : "1px solid #b7eb8f",
                                boxShadow: "0 1px 2px rgba(0,0,0,0.03)",
                            }}
                        >
                            <Title
                                level={5}
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    color: actionItem.resolution_status === ResolutionStatus.USER_REJECTED ? "#cf1322" : "#52c41a",
                                }}
                            >
                                {actionItem.resolution_status === ResolutionStatus.USER_REJECTED ? <CloseOutlined style={{ marginRight: 8 }} /> : <CheckOutlined style={{ marginRight: 8 }} />}
                                处理结果
                            </Title>
                            <Descriptions size="small" column={2}>
                                <Descriptions.Item label="处理状态">
                                    {actionItem.resolution_status && (RESOLUTION_STATUS_INFO[actionItem.resolution_status]?.label || actionItem.resolution_status)}
                                    {!actionItem.resolution_status && actionItem.processing_stage && (PROCESSING_STAGE_INFO[actionItem.processing_stage]?.label || actionItem.processing_stage)}
                                </Descriptions.Item>
                                {actionItem.resolved_at && <Descriptions.Item label="处理时间">{dayjs(actionItem.resolved_at).format("YYYY-MM-DD HH:mm:ss")}</Descriptions.Item>}
                                {actionItem.resolver_user_id && <Descriptions.Item label="处理人">{actionItem.resolver_user_id}</Descriptions.Item>}
                                {actionItem.user_remarks && (
                                    <Descriptions.Item label="处理备注" span={2}>
                                        {actionItem.user_remarks}
                                    </Descriptions.Item>
                                )}
                            </Descriptions>
                        </div>
                    )}
                </>
            )}
        </Card>
    );
};

export default NotificationDetailView;
